import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AUTH_STORAGE_KEY, getAuthCookie, getSharedCookieDomainAttribute } from './auth-session.util';
import {
  getAccessTokenExpiresAtMs,
  getAccessTokenJti,
  SessionLogoutReason,
} from './session-auth-error.util';

const LOG_PREFIX = '[CustomerAuth]';
export const AUTH_DEBUG_PERSIST_KEY = 'bh_customer_auth_debug_log';
const PERSIST_KEY = AUTH_DEBUG_PERSIST_KEY;
const MAX_EVENTS = 80;
const MAX_COOKIE_EVENTS = 20;

let sessionBuffer: AuthFlowLogEntry[] = [];

export type AuthFlowEvent =
  | 'logout_started'
  | 'logout_session_clearing'
  | 'redirect_to_login'
  | 'token_refresh_started'
  | 'token_refresh_success'
  | 'token_refresh_failed'
  | 'token_refresh_skipped_no_refresh_token'
  | 'token_refresh_queued'
  | 'http_401_detected'
  | 'http_403_detected'
  | 'http_error'
  | 'session_expiry_monitoring_started'
  | 'session_expiry_warning_scheduled'
  | 'session_expiry_modal_opened'
  | 'session_expiry_stay_clicked'
  | 'session_expiry_logout_clicked'
  | 'session_expiry_auto_logout'
  | 'inactivity_warning_shown'
  | 'inactivity_stay_clicked'
  | 'inactivity_logout_clicked'
  | 'inactivity_auto_logout'
  | 'auth_guard_redirect'
  | 'notify_session_logout';

interface AuthFlowLogEntry {
  ts: string;
  event: AuthFlowEvent;
  source: string;
  detail?: string;
  reason?: SessionLogoutReason | string;
  data?: Record<string, unknown>;
}

function isEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (environment.authFlowDebug === true) {
    return true;
  }
  if (environment.authFlowDebug === false) {
    return false;
  }
  return !environment.production || environment.logLevel === 'debug';
}

function maskToken(token: string | null | undefined): string | null {
  if (!token?.trim()) {
    return null;
  }
  const value = token.trim();
  if (value.length <= 12) {
    return '***';
  }
  return `...${value.slice(-12)}`;
}

function readAccessToken(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return (
    localStorage.getItem(AUTH_STORAGE_KEY) ??
    localStorage.getItem('accessToken') ??
    getAuthCookie(AUTH_STORAGE_KEY)
  );
}

function readRefreshToken(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem('refreshToken') ?? getAuthCookie('refreshToken');
}

function formatExpiry(token: string | null): string | null {
  if (!token) {
    return null;
  }
  const expiresAtMs = getAccessTokenExpiresAtMs(token);
  if (expiresAtMs === null) {
    return null;
  }
  return new Date(expiresAtMs).toISOString();
}

function remainingMs(token: string | null): number | null {
  if (!token) {
    return null;
  }
  const expiresAtMs = getAccessTokenExpiresAtMs(token);
  if (expiresAtMs === null) {
    return null;
  }
  return expiresAtMs - Date.now();
}

function readDebugCookie(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${PERSIST_KEY}=`;
  return (
    document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((cookie) => cookie.startsWith(prefix))
      .map((cookie) => decodeURIComponent(cookie.substring(prefix.length)))[0] ??
    null
  );
}

function writeDebugCookie(events: AuthFlowLogEntry[]): void {
  if (typeof document === 'undefined') {
    return;
  }
  try {
    const payload = JSON.stringify(events.slice(-MAX_COOKIE_EVENTS));
    if (payload.length > 3500) {
      return;
    }
    const domain = getSharedCookieDomainAttribute();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${PERSIST_KEY}=${encodeURIComponent(payload)}; path=/${domain}; max-age=86400; SameSite=Lax${secure}`;
  } catch {
    // Ignore cookie write failures during debug logging.
  }
}

function parseEvents(raw: string | null): AuthFlowLogEntry[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as AuthFlowLogEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadPersistedEvents(): AuthFlowLogEntry[] {
  if (sessionBuffer.length > 0) {
    return [...sessionBuffer];
  }
  if (typeof localStorage === 'undefined') {
    return parseEvents(readDebugCookie());
  }
  try {
    const raw =
      localStorage.getItem(PERSIST_KEY) ?? readDebugCookie();
    if (!raw) {
      return [];
    }
    const parsed = parseEvents(raw);
    sessionBuffer = parsed;
    return [...parsed];
  } catch {
    return [];
  }
}

function persistEvents(events: AuthFlowLogEntry[]): void {
  sessionBuffer = events.slice(-MAX_EVENTS);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(sessionBuffer));
    } catch {
      // Ignore quota errors during debug logging.
    }
  }
  writeDebugCookie(sessionBuffer);
}

function pushEvent(entry: AuthFlowLogEntry): void {
  const events = loadPersistedEvents();
  events.push(entry);
  persistEvents(events);
}

function snapshotAuthState(): Record<string, unknown> {
  const accessToken = readAccessToken();
  const refreshToken = readRefreshToken();
  return {
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    accessToken: maskToken(accessToken),
    refreshToken: maskToken(refreshToken),
    accessTokenJti: getAccessTokenJti(accessToken),
    accessTokenExpiresAt: formatExpiry(accessToken),
    accessTokenRemainingMs: remainingMs(accessToken),
    workspaceId:
      localStorage.getItem('workspace_id') ?? getAuthCookie('workspace_id'),
    userId: localStorage.getItem('id') ?? getAuthCookie('id'),
    cookieKeys:
      typeof document !== 'undefined'
        ? document.cookie
            .split(';')
            .map((c) => c.trim().split('=')[0])
            .filter(Boolean)
        : [],
    localStorageKeys:
      typeof localStorage !== 'undefined' ? Object.keys(localStorage) : [],
  };
}

function serializeHttpError(error: unknown): Record<string, unknown> {
  if (error instanceof HttpErrorResponse) {
    const body =
      error.error && typeof error.error === 'object'
        ? (error.error as Record<string, unknown>)
        : { message: error.message };
    return {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      code: typeof body['code'] === 'string' ? body['code'] : undefined,
      message:
        typeof body['message'] === 'string'
          ? body['message']
          : error.message,
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: String(error) };
}

export const authFlowDebug = {
  isEnabled,

  log(
    event: AuthFlowEvent,
    source: string,
    options?: {
      detail?: string;
      reason?: SessionLogoutReason | string;
      data?: Record<string, unknown>;
      level?: 'log' | 'warn' | 'error';
    },
  ): void {
    if (!isEnabled()) {
      return;
    }

    const entry: AuthFlowLogEntry = {
      ts: new Date().toISOString(),
      event,
      source,
      detail: options?.detail,
      reason: options?.reason,
      data: options?.data,
    };

    pushEvent(entry);

    const payload = {
      event,
      source,
      detail: options?.detail,
      reason: options?.reason,
      ...options?.data,
    };
    const level = options?.level ?? 'log';
    console[level](`${LOG_PREFIX} ${event}`, payload);
  },

  logAuthSnapshot(source: string, detail?: string): void {
    this.log('logout_session_clearing', source, {
      detail,
      data: snapshotAuthState(),
    });
  },

  logHttpError(
    event: AuthFlowEvent,
    source: string,
    error: unknown,
    extra?: Record<string, unknown>,
  ): void {
    this.log(event, source, {
      data: {
        ...serializeHttpError(error),
        ...extra,
      },
      level: event === 'http_401_detected' || event === 'http_403_detected'
        ? 'warn'
        : 'error',
    });
  },

  persistBeforeSessionClear(source: string, reason?: string): void {
    if (!isEnabled()) {
      return;
    }
    const events = loadPersistedEvents();
    events.push({
      ts: new Date().toISOString(),
      event: 'logout_session_clearing',
      source,
      reason,
      data: snapshotAuthState(),
    });
    sessionBuffer = events.slice(-MAX_EVENTS);
    persistEvents(sessionBuffer);
  },

  restoreAfterSessionClear(): void {
    if (!isEnabled()) {
      return;
    }
    if (sessionBuffer.length === 0) {
      return;
    }
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(PERSIST_KEY, JSON.stringify(sessionBuffer));
      } catch {
        // Ignore quota errors during debug logging.
      }
    }
    writeDebugCookie(sessionBuffer);
  },

  readFromCookie(): AuthFlowLogEntry[] {
    return parseEvents(readDebugCookie());
  },

  printFromLanding(): AuthFlowLogEntry[] {
    const fromCookie = this.readFromCookie();
    const fromStorage =
      typeof localStorage !== 'undefined'
        ? parseEvents(localStorage.getItem(PERSIST_KEY))
        : [];
    const events = fromCookie.length > 0 ? fromCookie : fromStorage;
    console.group(`${LOG_PREFIX} auth flow log on landing (${events.length})`);
    console.table(events);
    console.groupEnd();
    return events;
  },

  dump(): AuthFlowLogEntry[] {
    return loadPersistedEvents();
  },

  printDump(): void {
    if (!isEnabled()) {
      console.log(`${LOG_PREFIX} debug logging is disabled`);
      return;
    }
    const events = loadPersistedEvents();
    console.group(`${LOG_PREFIX} persisted auth flow log (${events.length})`);
    console.table(events);
    console.groupEnd();
  },

  clear(): void {
    sessionBuffer = [];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(PERSIST_KEY);
    }
    if (typeof document !== 'undefined') {
      const domain = getSharedCookieDomainAttribute();
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = `${PERSIST_KEY}=;expires=${expires};path=/${domain}; SameSite=Lax${secure}`;
      document.cookie = `${PERSIST_KEY}=;expires=${expires};path=/; SameSite=Lax${secure}`;
    }
  },
};

declare global {
  interface Window {
    __BH_CUSTOMER_AUTH_DEBUG__?: {
      dump: () => AuthFlowLogEntry[];
      print: () => void;
      printFromLanding: () => AuthFlowLogEntry[];
      readFromCookie: () => AuthFlowLogEntry[];
      clear: () => void;
    };
  }
}

if (typeof window !== 'undefined') {
  window.__BH_CUSTOMER_AUTH_DEBUG__ = {
    dump: () => authFlowDebug.dump(),
    print: () => authFlowDebug.printDump(),
    printFromLanding: () => authFlowDebug.printFromLanding(),
    readFromCookie: () => authFlowDebug.readFromCookie(),
    clear: () => authFlowDebug.clear(),
  };
}
